/**
 * A draft or a sent sentence, with its mentions drawn as mentions.
 *
 * One component for two surfaces on purpose. The composer draws it over the live `<textarea>` and
 * the thread draws it inside a sent bubble, and a mention has to be the same object in both — the
 * same blue, the same bounds, the same thing to press. Two renderers is how the sent message and
 * the draft it came from start disagreeing about what counts as a mention.
 *
 * ## What makes a mention pressable
 *
 * `target` — not the name, and not the source. A committed mention is plain text (see `mentions.ts`),
 * so the only way back to the thing it points at is to ask the container that owns the deck's rows.
 * A name it does not recognise, or one that names a capability rather than a row (`@Web`, `@Figma`),
 * comes back with nothing and is drawn as the same blue word with nothing behind it. That keeps the
 * affordance honest: the cursor changes exactly where there is something to open.
 */
import { Fragment, type CSSProperties, type KeyboardEvent, type MouseEvent } from 'react';
import { useMemo } from 'react';
import {
  segmentsFromRuns,
  splitMentions,
  type MentionRun,
  type MentionSegment,
} from './composer/mentions';
import styles from './MentionText.module.css';
import { cn } from '~/lib/utils';

/**
 * How a mention is written in: one word's fade, and the gap before the next word starts.
 *
 * `LiveProse` releases a word every frame and lets about ten overlap, because it has a paragraph to
 * roll through and the smoothness IS that overlap. A mention is two to five words, so at that beat
 * there is nothing to overlap and no reading of it as writing — which is why there is a stagger here
 * at all rather than the prose's own release.
 *
 * Both halved from the first pass, which ran at the prose's own 180/60 (Beka, 2026-09-20): a name
 * lands in the space of a keystroke, and at reading pace it felt waited for rather than typed. The
 * 3:1 RATIO is what makes it read as writing and is deliberately unchanged, so three words are still
 * in flight at any moment and the name simply arrives twice as soon — four words in 180ms, not 360.
 */
const REVEAL_MS = 90;
const REVEAL_STEP = 30;

export interface MentionTextProps {
  text: string;
  /**
   * Every name a mention could be wearing, for the surface that has to FIND them: a sent message is
   * a string off the wire and matching is the only thing anyone can do with it. Empty matches
   * nothing, which is the right answer before the deck's rows have loaded — the sentence still
   * reads, its mentions are simply still prose.
   *
   * Ignored when `runs` is given.
   */
  names?: readonly string[];
  /**
   * The mentions as RANGES, from a surface that knows what it inserted — the live draft, and only
   * it. Given these, no name is matched at all, so prose can never turn into a mention under the
   * caret by happening to spell one. See `remapRuns`.
   */
  runs?: readonly MentionRun[];
  /**
   * What a name opens, or `undefined` when nothing does. Called during render, so it must be cheap
   * and stable; the container memoises it against the rows it resolves from.
   */
  target?: (name: string) => (() => void) | undefined;
  /**
   * The `@` offset of the one run being written in. The composer's, and only ever one at a time —
   * which is the whole point of it being an offset rather than a flag. See `LiveComposer`.
   */
  revealAt?: number | null;
  /**
   * Keyboard-reachable. FALSE in the composer, where this is drawn inside an `aria-hidden` mirror
   * over the real field: a tab stop inside a hidden subtree is a trap, and the textarea under it
   * already owns the keyboard. The cost is that a mention in the DRAFT opens by pointer only, which
   * the same mention in the sent bubble does not share.
   */
  focusable?: boolean;
}

export function MentionText({
  text,
  names,
  runs,
  target,
  revealAt = null,
  focusable = true,
}: MentionTextProps) {
  const segments = useMemo(
    () => (runs ? segmentsFromRuns(text, runs) : splitMentions(text, names ?? [])),
    [names, runs, text],
  );
  return (
    <>
      {segments.map((segment) =>
        segment.name === undefined ? (
          <Fragment key={segment.start}>{segment.text}</Fragment>
        ) : (
          <Mention
            key={segment.start}
            segment={segment}
            open={target?.(segment.name)}
            revealing={segment.start === revealAt}
            focusable={focusable}
          />
        ),
      )}
    </>
  );
}

function Mention({
  segment,
  open,
  revealing,
  focusable,
}: {
  segment: MentionSegment;
  open: (() => void) | undefined;
  revealing: boolean;
  focusable: boolean;
}) {
  /*
   * Split on whitespace and KEEP it, so the gaps are tokens too — each one takes the delay of the
   * word it follows, which is what makes a space arrive with the word before it rather than ahead of
   * the word after it.
   */
  const body = revealing
    ? segment.text.split(/(\s+)/).map((token, index) => (
        <span
          key={index}
          className={styles.word}
          style={
            {
              '--live-word-duration': `${REVEAL_MS}ms`,
              animationDelay: `${Math.floor(index / 2) * REVEAL_STEP}ms`,
              /*
               * `backwards` is the whole reason a stagger reads as writing. Without it a word held
               * back by a delay paints at full strength through its wait, then snaps to `opacity: 0`
               * when its turn comes and fades up from there — so the name flashes whole before it is
               * written. This holds the 0% frame through the wait.
               */
              animationFillMode: 'backwards',
            } as CSSProperties
          }
        >
          {token}
        </span>
      ))
    : segment.text;

  if (!open) return <span className={styles.mention}>{body}</span>;

  return (
    <span
      className={cn(styles.mention, styles.open)}
      role="button"
      // -1 rather than omitted in the composer: the node still answers `.focus()` and a press, it
      // simply is not a stop on the way to the send button.
      tabIndex={focusable ? 0 : -1}
      // The press opens the thing; it must not also move the caret out from under the draft the
      // user is in the middle of writing, which is what a mousedown inside the field would do.
      onMouseDown={(event: MouseEvent) => event.preventDefault()}
      onClick={open}
      onKeyDown={(event: KeyboardEvent) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        // Space on a focused mention would otherwise scroll the thread out from under it.
        event.preventDefault();
        open();
      }}
    >
      {body}
    </span>
  );
}
